import { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useFetchETFHoldings, useMultiTickerPriceHistory } from '@etfSecurityHooks';

const TIME_RANGES: TimeRange[] = ['1W', '1M', '1Y', 'ALL'];

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value);
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { date, value } = payload[0].payload;
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">
        {new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>
      <p className="text-lg font-semibold">{formatCurrency(value)}</p>
    </div>
  );
}

export function ETFPortfolioTrendChart() {
  const [timePeriod, setTimePeriod] = useState<TimeRange>('1M');
  const { data: holdings = [], isLoading: holdingsLoading } = useFetchETFHoldings();

  const tickers = useMemo(
    () => [...new Set(holdings.map((h) => h.ticker_symbol))],
    [holdings]
  );

  const { data: priceHistory = [], isLoading: historyLoading } = useMultiTickerPriceHistory(
    tickers,
    timePeriod
  );

  const isLoading = holdingsLoading || (tickers.length > 0 && historyLoading);

  const chartData = useMemo(() => {
    if (holdings.length === 0 || priceHistory.length === 0) return [];

    // Build units map: ticker -> total units held
    const unitsMap: Record<string, number> = {};
    for (const h of holdings) {
      unitsMap[h.ticker_symbol] = (unitsMap[h.ticker_symbol] ?? 0) + h.units_held;
    }

    // Group price records by timestamp
    const byTimestamp: Record<string, Record<string, number>> = {};
    for (const record of priceHistory) {
      if (!byTimestamp[record.fetched_at]) byTimestamp[record.fetched_at] = {};
      byTimestamp[record.fetched_at][record.ticker_symbol] = record.price;
    }

    // For each timestamp, compute total portfolio value using last known price per ticker
    const sortedTimestamps = Object.keys(byTimestamp).sort();
    const lastKnownPrice: Record<string, number> = {};

    return sortedTimestamps.map((ts) => {
      // Update last known prices
      for (const [ticker, price] of Object.entries(byTimestamp[ts])) {
        lastKnownPrice[ticker] = price;
      }
      const total = tickers.reduce((sum, ticker) => {
        const price = lastKnownPrice[ticker] ?? 0;
        return sum + price * (unitsMap[ticker] ?? 0);
      }, 0);
      return { date: ts, value: total };
    });
  }, [holdings, priceHistory, tickers]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-semibold">Portfolio Value Over Time</CardTitle>
        <div className="flex gap-1" role="group" aria-label="Time range filter">
          {TIME_RANGES.map((range) => (
            <Button
              key={range}
              variant={timePeriod === range ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimePeriod(range)}
              aria-pressed={timePeriod === range}
            >
              {range}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[300px] flex items-end justify-between gap-2 px-4" aria-busy="true">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="w-full bg-muted animate-pulse rounded-t"
                style={{ height: `${40 + (i % 5) * 12}%` }} />
            ))}
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center" data-testid="empty-state">
            <p className="text-muted-foreground text-sm">
              No price history data available for the selected period.
            </p>
          </div>
        ) : (
          <div role="img"
            aria-label={`ETF portfolio value trend chart for ${timePeriod} period with ${chartData.length} data points`}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tickFormatter={formatDate} className="text-xs" />
                <YAxis tickFormatter={formatCurrency} className="text-xs" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="value"
                  stroke="hsl(var(--primary))" strokeWidth={2}
                  fill="url(#portfolioGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
